// Client-side indicator runner — ported from server/indicator-runner.ts
// Compiles and executes custom or default indicator code using new Function()
// Uses IndexedDB for persistent storage of custom code and disabled states

import type { JiraIssue, LagIndicatorResult, IndicatorConfig } from "./types";
import { DEFAULT_INDICATORS } from "./indicator-defaults";
import {
  getStatusConfigSync,
  getStatusStrings,
  isDoneStatus as _isDoneStatus,
  statusMatchesCategory,
} from "./jira-status-config";

// Build a STATUS-compatible object from the configurable status config.
// This is a getter so it always reflects the latest config.
function getSTATUS() {
  const cfg = getStatusConfigSync();
  return {
    TODO: cfg.categories.todo,
    IN_PROGRESS: cfg.categories.in_progress,
    RELEASE_CANDIDATE: cfg.categories.release_candidate,
    DEPLOYMENT_FAILED: cfg.categories.blocked.filter(s => s.includes('deployment failed')),
    BLOCKED: cfg.categories.blocked,
    TESTING: cfg.categories.testing,
    READY: cfg.categories.release_candidate.filter(s => s.includes('ready')),
    DEV_REWORK: cfg.categories.rework,
    DONE: cfg.categories.done,
  };
}

function statusMatches(status: string, group: readonly string[]): boolean {
  const lower = status.toLowerCase().trim();
  return group.some((s) => lower === s || lower.includes(s));
}

function isTestingStatus(status: string): boolean {
  return statusMatchesCategory(status, 'testing');
}

function isDevReworkStatus(status: string): boolean {
  return statusMatchesCategory(status, 'rework');
}

function isReleaseCandidateStatus(status: string): boolean {
  return statusMatchesCategory(status, 'release_candidate');
}

function isDoneStatus(status: string): boolean {
  return _isDoneStatus(status);
}

function isInProgressStatus(status: string): boolean {
  return statusMatchesCategory(status, 'in_progress');
}

function isProductionHotfix(status: string): boolean {
  return status.toLowerCase().trim() === "production hotfix";
}

// The helpers object passed to custom indicator code
// STATUS is computed dynamically so custom code always gets the latest config
const HELPERS = {
  get STATUS() { return getSTATUS(); },
  statusMatches,
  isTestingStatus,
  isDevReworkStatus,
  isReleaseCandidateStatus,
  isDoneStatus,
  isInProgressStatus,
  isProductionHotfix,
  getStatusStrings,
};

// In-memory storage (loaded from IndexedDB on init, synced back on change)
let customIndicatorCode: Map<number, string> = new Map();
let disabledIndicators: Set<number> = new Set();
let userCreatedIndicators: Map<number, IndicatorConfig> = new Map();
let nextCustomId = 100;
let initialized = false;

// Initialize from IndexedDB
export async function initIndicators(): Promise<void> {
  if (initialized) return;

  try {
    // Dynamic import to avoid circular dependency
    const storage = await import("./storage");

    const storedCode = await storage.getCustomIndicatorCode();
    for (const [idStr, code] of Object.entries(storedCode)) {
      customIndicatorCode.set(Number(idStr), code);
    }

    const disabledIds = await storage.getDisabledIndicatorIds();
    disabledIndicators = new Set(disabledIds);

    const storedIndicators = await storage.getStoredIndicators();
    for (const ind of storedIndicators) {
      userCreatedIndicators.set(ind.id, ind);
      if (ind.id >= nextCustomId) {
        nextCustomId = ind.id + 1;
      }
    }

    initialized = true;
    console.log(
      `[IndicatorRunner] Initialized: ${customIndicatorCode.size} custom code overrides, ${disabledIndicators.size} disabled, ${userCreatedIndicators.size} user-created`
    );
  } catch (e) {
    console.warn("[IndicatorRunner] Failed to load from IndexedDB, using defaults:", e);
    initialized = true;
  }
}

// ==================== Custom Code ====================

export function getCustomIndicators(): Record<number, string> {
  const result: Record<number, string> = {};
  for (const [id, code] of customIndicatorCode) {
    result[id] = code;
  }
  return result;
}

export async function setCustomIndicatorCode(id: number, code: string): Promise<void> {
  customIndicatorCode.set(id, code);
  try {
    const storage = await import("./storage");
    await storage.setCustomIndicatorCodeEntry(id, code);
  } catch { /* IndexedDB unavailable */ }
}

export async function deleteCustomIndicatorCode(id: number): Promise<void> {
  customIndicatorCode.delete(id);
  try {
    const storage = await import("./storage");
    await storage.deleteCustomIndicatorCodeEntry(id);
  } catch { /* IndexedDB unavailable */ }
}

export async function resetAllCustomIndicators(): Promise<void> {
  customIndicatorCode.clear();
  try {
    const storage = await import("./storage");
    await storage.resetAllIndicatorCode();
  } catch { /* IndexedDB unavailable */ }
}

// ==================== Enabled/Disabled ====================

export function isIndicatorEnabled(id: number): boolean {
  return !disabledIndicators.has(id);
}

export async function setIndicatorEnabled(id: number, enabled: boolean): Promise<void> {
  if (enabled) {
    disabledIndicators.delete(id);
  } else {
    disabledIndicators.add(id);
  }
  try {
    const storage = await import("./storage");
    await storage.setDisabledIndicatorIds(Array.from(disabledIndicators));
  } catch { /* IndexedDB unavailable */ }
}

export function getDisabledIndicators(): number[] {
  return Array.from(disabledIndicators);
}

// ==================== User-created custom indicators ====================

export async function createIndicator(
  config: Omit<IndicatorConfig, "id">
): Promise<IndicatorConfig> {
  const id = nextCustomId++;
  const indicator: IndicatorConfig = { id, ...config };
  userCreatedIndicators.set(id, indicator);
  try {
    const storage = await import("./storage");
    await storage.saveIndicator(indicator);
  } catch { /* IndexedDB unavailable */ }
  return indicator;
}

export async function deleteIndicator(id: number): Promise<boolean> {
  if (!userCreatedIndicators.has(id)) return false;
  userCreatedIndicators.delete(id);
  customIndicatorCode.delete(id);
  disabledIndicators.delete(id);
  try {
    const storage = await import("./storage");
    await storage.deleteStoredIndicator(id);
    await storage.setDisabledIndicatorIds(Array.from(disabledIndicators));
  } catch { /* IndexedDB unavailable */ }
  return true;
}

export async function updateIndicatorConfig(
  id: number,
  updates: Partial<Pick<IndicatorConfig, "scope" | "source">>
): Promise<boolean> {
  const config = getIndicatorConfig(id);
  if (!config) return false;

  // For user-created indicators, update in-place
  if (userCreatedIndicators.has(id)) {
    const updated = { ...userCreatedIndicators.get(id)!, ...updates };
    userCreatedIndicators.set(id, updated);
    try {
      const storage = await import("./storage");
      await storage.saveIndicator(updated);
    } catch { /* IndexedDB unavailable */ }
    return true;
  }

  // For default indicators, store override config
  // We store the full config with updates as a user-created entry
  const overridden = { ...config, ...updates };
  userCreatedIndicators.set(id, overridden);
  try {
    const storage = await import("./storage");
    await storage.saveIndicator(overridden);
  } catch { /* IndexedDB unavailable */ }
  return true;
}

export function getUserCreatedIndicators(): IndicatorConfig[] {
  return Array.from(userCreatedIndicators.values());
}

export function isUserCreated(id: number): boolean {
  return userCreatedIndicators.has(id);
}

// ==================== Combined accessors ====================

export function getAllIndicatorConfigs(): IndicatorConfig[] {
  return [...DEFAULT_INDICATORS, ...userCreatedIndicators.values()];
}

export function getIndicatorConfig(id: number): IndicatorConfig | undefined {
  return DEFAULT_INDICATORS.find((i: IndicatorConfig) => i.id === id) || userCreatedIndicators.get(id);
}

export function getEffectiveCode(id: number): string {
  if (customIndicatorCode.has(id)) {
    return customIndicatorCode.get(id)!;
  }
  const config = getIndicatorConfig(id);
  return config?.code || "";
}

// ==================== Security Sandbox ====================
//
// Indicator code is user-supplied JavaScript executed via new Function().
// Without sandboxing, this is essentially eval() with full access to the
// browser's global scope (fetch, indexedDB, document, window, etc.).
//
// Security model (two layers):
//
// Layer 1 — Static Analysis:
//   Scan code for dangerous patterns before execution. Any match blocks
//   the code from running. Covers network APIs, dynamic code generation,
//   DOM access, storage APIs, prototype chain escapes, and more.
//
// Layer 2 — Parameter Shadowing:
//   All dangerous global names are added as extra Function parameters
//   and passed as undefined. Inside the function body, references like
//   `fetch`, `window`, `indexedDB` resolve to the parameter (undefined),
//   not the global. This is a proven JS sandboxing technique.
//
// Known limitations (actively being addressed — see TODO.md § Security):
//   - Prototype chain escape: `[].constructor.constructor('return fetch')()`
//     can reconstruct the Function constructor from any object. The static
//     analysis catches `.constructor.constructor` patterns but obfuscated
//     variants could bypass it.
//   - REMEDIATION IN PROGRESS: Web Worker execution with frozen prototypes
//     will close this gap. Indicator code will run in an isolated Worker
//     where dangerous globals are deleted before execution, and
//     Object/Array/Function prototypes are frozen to prevent traversal.
//
// Threat model:
//   The primary threat is imported indicator code from untrusted sources.
//   Users writing their own code on their own machine is lower risk.

// Dangerous patterns that BLOCK execution (Layer 1)
const BLOCKED_PATTERNS: [RegExp, string][] = [
  // Network access
  [/\bfetch\s*\(/, 'fetch() is blocked — indicators cannot make network requests'],
  [/\bfetch\s*`/, 'fetch with template literal is blocked'],
  [/\bXMLHttpRequest\b/, 'XMLHttpRequest is blocked'],
  [/\bWebSocket\b/, 'WebSocket is blocked'],
  [/\bEventSource\b/, 'EventSource is blocked'],
  [/\bsendBeacon\b/, 'sendBeacon is blocked'],
  [/\bRequest\s*\(/, 'Request constructor is blocked'],
  [/\bResponse\s*\(/, 'Response constructor is blocked'],
  // Dynamic code execution
  [/\beval\s*\(/, 'eval() is blocked'],
  [/\bnew\s+Function\b/, 'new Function() is blocked'],
  [/\bFunction\s*\(/, 'Function() constructor is blocked'],
  [/\bsetTimeout\b/, 'setTimeout is blocked — indicators must be synchronous'],
  [/\bsetInterval\b/, 'setInterval is blocked — indicators must be synchronous'],
  [/\bqueueMicrotask\b/, 'queueMicrotask is blocked'],
  // Import / require
  [/\bimport\s*\(/, 'Dynamic import() is blocked'],
  [/\brequire\s*\(/, 'require() is blocked'],
  [/\bimportScripts\b/, 'importScripts is blocked'],
  // DOM access
  [/\bdocument\s*[.[]/,  'DOM access (document) is blocked'],
  [/\bwindow\s*[.[]/,    'window access is blocked'],
  // Storage
  [/\bindexedDB\b/, 'IndexedDB access is blocked'],
  [/\blocalStorage\b/, 'localStorage access is blocked'],
  [/\bsessionStorage\b/, 'sessionStorage access is blocked'],
  [/\bcaches\s*[.[]/,  'Cache API access is blocked'],
  // Prototype chain escape
  [/\.constructor\s*\.\s*constructor/, 'Constructor chain traversal is blocked (potential sandbox escape)'],
  [/\.__proto__\b/, '__proto__ access is blocked'],
  [/Object\s*\.\s*getPrototypeOf\b/, 'Object.getPrototypeOf is blocked'],
  [/Object\s*\.\s*setPrototypeOf\b/, 'Object.setPrototypeOf is blocked'],
  [/Object\s*\.\s*defineProperty\b/, 'Object.defineProperty is blocked'],
  [/Object\s*\.\s*defineProperties\b/, 'Object.defineProperties is blocked'],
  [/\bReflect\s*\./, 'Reflect API is blocked'],
  [/\bnew\s+Proxy\b/, 'Proxy is blocked'],
  // Global scope access
  [/\bglobalThis\b/, 'globalThis access is blocked'],
  // Workers / messaging
  [/\bnew\s+Worker\b/, 'Worker creation is blocked'],
  [/\bnew\s+SharedWorker\b/, 'SharedWorker is blocked'],
  [/\bServiceWorker\b/, 'ServiceWorker access is blocked'],
  [/\bBroadcastChannel\b/, 'BroadcastChannel is blocked'],
  // Cookies
  [/\bdocument\s*\.\s*cookie\b/, 'Cookie access is blocked'],
  [/\bcookieStore\b/, 'CookieStore access is blocked'],
];

// Globals to shadow as undefined function parameters (Layer 2)
const SHADOWED_GLOBALS = [
  // Network
  'fetch', 'XMLHttpRequest', 'WebSocket', 'EventSource', 'Request', 'Response',
  // Code execution
  'eval', 'Function',
  // Timers
  'setTimeout', 'setInterval', 'setImmediate', 'queueMicrotask', 'requestAnimationFrame',
  // Import
  'importScripts',
  // DOM / Window
  'document', 'window', 'self', 'globalThis', 'top', 'parent', 'frames',
  // Storage
  'indexedDB', 'localStorage', 'sessionStorage', 'caches', 'cookieStore',
  // Navigation / location
  'navigator', 'location', 'history',
  // Dangerous constructors
  'Proxy', 'Reflect',
  // Workers / messaging
  'Worker', 'SharedWorker', 'ServiceWorker', 'BroadcastChannel', 'MessageChannel', 'MessagePort',
  // Notifications / misc
  'Notification', 'alert', 'confirm', 'prompt', 'open', 'close',
  // Crypto (prevent exfil of generated keys)
  'crypto', 'CryptoKey', 'SubtleCrypto',
  // Performance (can be used for timing attacks)
  'performance', 'PerformanceObserver',
] as const;

const SHADOW_UNDEFINED = SHADOWED_GLOBALS.map(() => undefined);

export function validateIndicatorSecurity(code: string): { safe: boolean; warnings: string[] } {
  const warnings: string[] = [];
  for (const [pattern, message] of BLOCKED_PATTERNS) {
    if (pattern.test(code)) {
      warnings.push(message);
    }
  }
  return { safe: warnings.length === 0, warnings };
}

// ==================== Execution ====================

export function runIndicator(
  id: number,
  issues: JiraIssue[],
  targetDevs: string[],
  devMap: Map<string, string>,
  devInfo: Map<string, { commits: number; pullRequests: number }> = new Map()
): LagIndicatorResult {
  const code = getEffectiveCode(id);
  if (!code) {
    throw new Error(`No code found for indicator ${id}`);
  }

  // Layer 1: Static analysis — block dangerous patterns
  const security = validateIndicatorSecurity(code);
  if (!security.safe) {
    console.error(`[IndicatorRunner] Security violation in indicator ${id}:`, security.warnings);
    const errConfig = getIndicatorConfig(id);
    return {
      id,
      name: errConfig?.name || `Indicator ${id}`,
      description: `Blocked: ${security.warnings[0]}`,
      scope: errConfig?.scope || "per-user",
      results: [],
      overallValue: 0,
      overallLabel: "Blocked",
      severity: "critical" as const,
    };
  }

  try {
    // Layer 2: Parameter shadowing — all dangerous globals resolve to undefined
    const fn = new Function(
      "issues",
      "targetDevs",
      "devMap",
      "helpers",
      "devInfo",
      ...SHADOWED_GLOBALS,
      code
    );

    const result = fn(issues, targetDevs, devMap, HELPERS, devInfo, ...SHADOW_UNDEFINED);

    if (!result || typeof result.id !== "number" || !result.name || !Array.isArray(result.results)) {
      throw new Error(
        `Indicator ${id} returned invalid result. Must have: id (number), name (string), results (array), overallValue, overallLabel, severity`
      );
    }

    // Normalize: custom code may return overallSeverity instead of severity
    if (!result.severity && result.overallSeverity) {
      result.severity = result.overallSeverity;
    }
    if (!result.severity) {
      result.severity = "warning";
    }

    const config = getIndicatorConfig(id);
    result.scope = config?.scope || "per-user";

    return result as LagIndicatorResult;
  } catch (err: unknown) {
    const e = err as Error;
    console.error(`[IndicatorRunner] Error running indicator ${id}:`, e.message);
    const errConfig = getIndicatorConfig(id);
    return {
      id,
      name: errConfig?.name || `Indicator ${id}`,
      description: `Error: ${e.message}`,
      scope: errConfig?.scope || "per-user",
      results: [],
      overallValue: 0,
      overallLabel: "Error",
      severity: "critical" as const,
    };
  }
}

export function runAllIndicators(
  type: "lag" | "lead",
  issues: JiraIssue[],
  targetDevs: string[],
  devMap: Map<string, string>,
  devInfo: Map<string, { commits: number; pullRequests: number }> = new Map(),
  scope?: "per-user" | "overall"
): LagIndicatorResult[] {
  const allConfigs = getAllIndicatorConfigs();
  const indicators = allConfigs.filter((i) =>
    i.type === type && isIndicatorEnabled(i.id) && (!scope || (i.scope || "per-user") === scope)
  );
  return indicators.map((indicator) =>
    runIndicator(indicator.id, issues, targetDevs, devMap, devInfo)
  );
}

export function indicatorNeedsDevInfo(id: number): boolean {
  const config = getIndicatorConfig(id);
  return config?.needsDevInfo ?? false;
}

export function getDevInfoIndicatorIds(type: "lag" | "lead", scope?: "per-user" | "overall"): number[] {
  return getAllIndicatorConfigs()
    .filter((i) => i.type === type && i.needsDevInfo && isIndicatorEnabled(i.id) && (!scope || (i.scope || "per-user") === scope))
    .map((i) => i.id);
}

export function validateIndicatorCode(code: string): { valid: boolean; error?: string; securityWarnings?: string[] } {
  // Security check first
  const security = validateIndicatorSecurity(code);
  if (!security.safe) {
    return { valid: false, error: `Security: ${security.warnings[0]}`, securityWarnings: security.warnings };
  }
  // Syntax check with shadowed globals
  try {
    new Function("issues", "targetDevs", "devMap", "helpers", "devInfo", ...SHADOWED_GLOBALS, code);
    return { valid: true };
  } catch (err: unknown) {
    const e = err as Error;
    return { valid: false, error: e.message };
  }
}
